/**
 * Blog post manifest (Story 30.10). Metadata (frontmatter + reading time + TOC)
 * comes from `virtual:blog-meta` — the build-side content scan (single source
 * of truth, shared with the sitemap source + RSS). The compiled MDX components
 * come from import.meta.glob and are matched to their metadata by slug.
 */
import blogMeta from "virtual:blog-meta";
import type { ComponentType } from "react";
import type { BlogFrontmatter, BlogPost } from "./types";

interface MdxModule {
  default: ComponentType;
  frontmatter: BlogFrontmatter;
}

const modules = import.meta.glob<MdxModule>("../../content/blog/*.mdx", { eager: true });

/** filename (sans .mdx) → compiled component. */
const componentBySlug = new Map<string, ComponentType>();
for (const [path, mod] of Object.entries(modules)) {
  const fileSlug = path.split("/").pop()!.replace(/\.mdx$/, "");
  const slug = (mod.frontmatter?.slug as string) || fileSlug;
  componentBySlug.set(slug, mod.default);
}

const ALL_POSTS: BlogPost[] = blogMeta.map((meta) => ({
  frontmatter: meta.frontmatter as BlogFrontmatter,
  slug: meta.slug,
  Component: componentBySlug.get(meta.slug) ?? (() => null),
  readingTimeMinutes: meta.readingTimeMinutes,
  toc: meta.toc,
}));

/** All PUBLISHED posts (drafts excluded), newest first. */
export function getAllPosts(): BlogPost[] {
  return ALL_POSTS.filter((p) => !p.frontmatter.draft);
}

/** A post by slug, INCLUDING drafts (route renders them for preview). */
export function getPostBySlug(slug: string): BlogPost | undefined {
  return ALL_POSTS.find((p) => p.slug === slug);
}

/** Up to `limit` published posts sharing a category or tag with `post`. */
export function getRelatedPosts(post: BlogPost, limit = 3): BlogPost[] {
  // Defensive: frontmatter is normalized at scan time (tags is always an
  // array), but guard here too so a hand-constructed post can't crash the view.
  const myTags = post.frontmatter.tags ?? [];
  return getAllPosts()
    .filter((p) => p.slug !== post.slug)
    .map((p) => {
      const sharedTags = (p.frontmatter.tags ?? []).filter((t) => myTags.includes(t)).length;
      const sameCategory = p.frontmatter.category === post.frontmatter.category ? 1 : 0;
      return { p, score: sharedTags + sameCategory };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((x) => x.p);
}

export type { BlogPost, BlogFrontmatter } from "./types";
